import { Document } from '@contentful/rich-text-types'
import {
  SideBySideResponsive,
  SideBySideResponsiveProps,
} from '@awazevr/design-system.components.side-by-side-responsive'
import {
  ResponsiveImage,
  ResponsiveImageProps,
} from '@/components/ResponsiveImage'
import RichText from '@/components/RichText/RichText'
import { FC, useContext } from 'react'
import { GlobalProps, GlobalPropsInterface } from 'src/utils/globalProps'
import { trackOptimizelyClientsideEvent } from 'src/lib/optimizely/optimizely-for-client'

export interface SideBySideModuleProps
  extends Omit<Omit<SideBySideResponsiveProps, 'image'>, 'text'> {
  image: ResponsiveImageProps
  textColor?: 'light' | 'dark'
  text?: Document
  heroSection?: boolean
  optimizelyCtaEventKey?: string
}

export const SideBySideModule: FC<SideBySideModuleProps> = ({
  image,
  textColor,
  text,
  heroSection,
  actions,
  optimizelyCtaEventKey,
  ...props
}) => {
  // 90% of the side-by-side modules are used as a hero section.
  // If the value in Contentful is undefined, it means that it should NOT be lazy-loaded.
  const lazyLoadingDisable = heroSection !== false

  const { optimizelySdkKey, brand, contentfulLocale, host, awazeClient } =
    useContext(GlobalProps)

  const actionsWithHandlers = attachOptimizelyClickHandlers({
    actions,
    optimizelyCtaEventKey,
    optimizelySdkKey,
    brand,
    contentfulLocale,
    host,
    awazeClient,
  })

  return (
    <SideBySideResponsive
      {...props}
      actions={actionsWithHandlers}
      text={
        text && (
          <RichText
            content={text}
            textColor={
              textColor === 'light'
                ? 'ColorTextBodyOnDark'
                : 'ColorTextBodyOnWhite'
            }
          />
        )
      }
      image={<ResponsiveImage {...image} lazyLoading={!lazyLoadingDisable} />}
    />
  )
}

function attachOptimizelyClickHandlers({
  actions,
  optimizelyCtaEventKey,
  optimizelySdkKey,
  brand,
  contentfulLocale,
  host,
  awazeClient,
}: {
  actions: SideBySideResponsiveProps['actions']
  optimizelyCtaEventKey: string | undefined
} & Pick<
  GlobalPropsInterface,
  'optimizelySdkKey' | 'brand' | 'contentfulLocale' | 'host' | 'awazeClient'
>): SideBySideResponsiveProps['actions'] {
  if (!optimizelyCtaEventKey || !actions) return actions

  return actions.map((item) => {
    const track = () => {
      trackOptimizelyClientsideEvent({
        optimizelySdkKey,
        eventKey: optimizelyCtaEventKey,
        brand,
        locale: contentfulLocale,
        host,
        awazeClient,
      })
    }

    switch (item.type) {
      case 'link':
        return {
          ...item,
          action: {
            ...item.action,
            linkProps: { ...item.action.linkProps, onClick: track },
          },
        }
      case 'button':
        return {
          ...item,
          action: { ...item.action, onClick: track },
        }
    }
  })
}
